import { tx } from "../../lib/i18n";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Search, BadgeCheck, Video, Users, Lock, ArrowRight, CreditCard, Laptop } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { useMeta } from "../../lib/useMeta";
import { PhotoPlaceholder } from "../site/Placeholder";

const Field = ({ label, value, onChange, options, placeholder, testid }) => (
  <label className="relative flex min-w-0 flex-1 flex-col rounded-2xl border border-slate-200 bg-white px-4 py-2 shadow-sm transition-[border-color] focus-within:border-osk-blue">
    <span className="text-xs font-semibold text-osk-navy">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full appearance-none bg-transparent pr-6 text-[15px] font-medium text-osk-navy outline-none">
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
    </select>
    <ChevronDown className="pointer-events-none absolute bottom-3 right-3 h-5 w-5 text-osk-navy" />
  </label>
);

export const SearchBar = () => {
  const { t } = useI18n();
  const meta = useMeta();
  const nav = useNavigate();
  const [island, setIsland] = useState("aruba");
  const [school, setSchool] = useState("");
  const [subject, setSubject] = useState("");
  const go = () => {
    const p = new URLSearchParams({ island });
    if (school) p.set("school_type", school);
    if (subject) p.set("subject", subject);
    nav(`/lessen?${p}`);
  };
  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-stretch md:rounded-[28px] md:bg-white md:p-3 md:shadow-[0_10px_30px_rgba(11,42,91,0.10)]" data-testid="hero-search">
      <Field label={t("search.island")} value={island} onChange={setIsland} testid="search-island-select"
        options={(meta?.islands || [{ code: "aruba", name: "Aruba", enabled: true }]).map((i) => ({ value: i.code, label: i.enabled ? i.name : `${i.name} ${tx("(binnenkort)")}`, disabled: !i.enabled }))} />
      <Field label={t("search.school")} value={school} onChange={setSchool} placeholder={t("search.school_ph")} testid="search-school-select"
        options={(meta?.school_types || []).map((s) => ({ value: s, label: s }))} />
      <Field label={t("search.subject")} value={subject} onChange={setSubject} placeholder={t("search.subject_ph")} testid="search-subject-select"
        options={(meta?.subjects || []).map((s) => ({ value: s, label: s }))} />
      <button onClick={go} className="osk-btn-primary whitespace-nowrap text-base" data-testid="search-submit-btn">
        <Search className="h-5 w-5" />{t("search.btn")}
      </button>
    </div>
  );
};

export const Hero = () => {
  const { t } = useI18n();
  return (
    <section className="relative overflow-hidden bg-gradient-to-r from-[#F4F8FE] via-white to-transparent" data-testid="hero-section">
      <div className="pointer-events-none absolute -left-24 top-10 h-72 w-48 rounded-full bg-osk-light" />
      <div className="osk-container relative grid items-center gap-6 py-8 md:grid-cols-[1.05fr_1fr] md:gap-4 md:py-12">
        <div className="osk-rise z-10 space-y-5">
          <h1 className="text-[32px] leading-[1.12] sm:text-5xl lg:text-[50px]" data-testid="hero-title">
            {t("hero.title1")}<span className="osk-highlight">{t("hero.highlight")}</span>{t("hero.title2")}
          </h1>
          <p className="max-w-xl text-[15px] leading-relaxed text-osk-text md:text-lg">{t("hero.sub")}</p>
          <div className="md:hidden"><PhotoPlaceholder label="Hero foto" className="h-48 w-full rounded-2xl" testid="hero-image-mobile" /></div>
          <div className="md:-mr-24 md:pr-0"><SearchBar /></div>
        </div>
        <div className="relative hidden h-[460px] md:block">
          <PhotoPlaceholder label="Hero foto" className="absolute inset-y-0 -right-32 left-0 rounded-l-[40px]" testid="hero-image" />
        </div>
      </div>
    </section>
  );
};

const TRUST = [
  { icon: BadgeCheck, color: "text-osk-orange", k: 1 },
  { icon: Video, color: "text-osk-blue", k: 2 },
  { icon: Users, color: "text-osk-blue", k: 3 },
  { icon: Lock, color: "text-osk-orange", k: 4 },
];

export const SentooBadge = () => (
  <div className="flex h-16 w-28 shrink-0 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white text-sm font-extrabold text-osk-navy" data-testid="sentoo-logo-placeholder">
    <span className="text-[10px] font-semibold uppercase tracking-widest text-osk-muted">logo</span>Sentoo
  </div>
);

export const TrustStrip = () => {
  const { t } = useI18n();
  return (
    <section className="osk-container" data-testid="trust-strip">
      <div className="hidden items-center gap-4 rounded-2xl bg-osk-light px-6 py-5 md:flex">
        {TRUST.map(({ icon: Icon, color, k }, i) => (
          <div key={k} className={`flex flex-1 items-start gap-3 ${i ? "border-l border-slate-300/70 pl-5" : ""}`} data-testid={`trust-item-${k}`}>
            <Icon className={`h-8 w-8 shrink-0 ${color}`} strokeWidth={1.8} />
            <div><p className="text-[15px] font-bold text-osk-navy">{t(`trust.${k}t`)}</p><p className="text-sm text-osk-text">{t(`trust.${k}d`)}</p></div>
          </div>
        ))}
        <SentooBadge />
      </div>
      <div className="grid grid-cols-4 gap-1 rounded-2xl bg-osk-light p-3 md:hidden">
        {TRUST.map(({ icon: Icon, color, k }, i) => (
          <div key={k} className={`flex flex-col items-center gap-1.5 text-center ${i ? "border-l border-slate-300/70" : ""}`} data-testid={`trust-item-mobile-${k}`}>
            <Icon className={`h-6 w-6 ${color}`} strokeWidth={1.8} />
            <span className="text-[11px] font-semibold leading-tight text-osk-navy">{t(`trust.${k}s`)}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

const STEPS = [{ k: 1, icon: Search }, { k: 2, icon: CreditCard }, { k: 3, icon: Laptop }];

export const HowSteps = () => {
  const { t } = useI18n();
  return (
    <section className="osk-container py-10" data-testid="how-section">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:gap-10">
        <div className="shrink-0">
          <h2 className="text-2xl md:text-[32px]">{t("how.title")}</h2>
          <p className="mt-1 hidden text-osk-text md:block">{t("how.sub")}</p>
        </div>
        <div className="flex flex-1 flex-col gap-4 md:flex-row md:items-center">
          {STEPS.map(({ k, icon: Icon }, i) => (
            <div key={k} className="flex flex-1 items-center gap-3" data-testid={`how-step-${k}`}>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-osk-orange text-base font-extrabold text-white">{k}</span>
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white shadow-[0_4px_14px_rgba(11,42,91,0.10)]"><Icon className="h-6 w-6 text-osk-blue" /></span>
              <div className="min-w-0"><p className="font-bold text-osk-navy">{t(`how.${k}t`)}</p><p className="text-sm text-osk-text">{t(`how.${k}d`)}</p></div>
              {i < 2 && <ArrowRight className="ml-auto hidden h-5 w-5 shrink-0 text-osk-blue md:block" />}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
